<template>
  <div class="row">
    <model-index
      ref="table"
      class="col-12"
      :loading="loading"
      v-bind="$props"
      :columns="columns"
      :busy.sync="busy"
      :models-paginated="modelsPaginated"
      :models="models"
      :page.sync="page"
      :per-page.sync="perPage"
      :from="from"
      :total="totalFiltered"
      :search.sync="search"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      :empty-state-title="$t('sale_notes.empty_state.title')"
      empty-state-subtitle=" "
      @create-click="handleCreate"
      @show-click="handleShow"
      @edit-click="handleEdit"
      @delete-click="handleDelete"
    >
      <template #card-header="{ cardAttrs }">
        <b-card-title>{{ cardAttrs.title || `${$t('List')} ${cardTitle}` }}</b-card-title>
        <b-card-sub-title v-if="cardAttrs.subTitle">{{ cardAttrs.subTitle }}</b-card-sub-title>
        <div class="d-flex flex-wrap align-items-center gap-2 mt-2">
          
          <b-button
              v-if="hasPermissionToSubmitApprovals"
              :disabled="isAnyBulkActionLoading || !canSubmitSelected"
              size="sm"
              variant="primary"
              @click="handleBulkSubmit"
          >
            <i v-if="bulkSubmitLoading" class="bx bx-loader bx-spin"></i>
            {{ lodash.startCase($t('balancing_approvals.buttons.submit')) }}
          </b-button>
          <b-button
              :disabled="isAnyBulkActionLoading || !canRejectSelected"
              size="sm"
              variant="danger"
              @click="handleBulkReject"
          >
            <i v-if="bulkRejectLoading" class="bx bx-loader bx-spin"></i>
            {{ lodash.startCase($t('balancing_approvals.buttons.reject')) }}
          </b-button>
          <b-button
              :disabled="isAnyBulkActionLoading || !canApproveSelected"
              size="sm"
              variant="success"
              @click="handleBulkApprove"
          >
            <i v-if="bulkApproveLoading" class="bx bx-loader bx-spin"></i>
            {{ lodash.startCase($t('balancing_approvals.buttons.approve')) }}
          </b-button>
          <div v-if="selectedBalancingIds.length" class="font-size-14 ms-auto">
            <span>{{ selectedBalancingIds.length }} Terpilih</span>
            <b-link class="ms-2 text-danger" @click="clearSelectedBalancing">{{ $t('Cancel') }}</b-link>
          </div>
        </div>
      </template>
      <template #head-balancing>
        <div>Nominal Balancing</div>
      </template>
    </model-index>

    <balancing-approval-submit-confirmation ref="balancing-approval-submit-confirmation" />
    <balancing-approval-approve-confirmation ref="balancing-approval-approve-confirmation" />
    <balancing-approval-reject-confirmation ref="balancing-approval-reject-confirmation" />
    <balancing-approval-bulk-action-success
        ref="balancing-approval-bulk-action-success"
        @hidden="handleBulkActionResultModalHidden"
    />
    <balancing-approval-bulk-action-partial-success
        ref="balancing-approval-bulk-action-partial-success"
        @hidden="handleBulkActionResultModalHidden"
    />
  </div>
</template>

<script>
import ModelChildIndex from "../../models/children/index";
import BalancingApprovalSubmitConfirmation from "@/components/modals/balancing-approval-submit-confirmation.vue";
import BalancingApprovalApproveConfirmation from "@/components/modals/balancing-approval-approve-confirmation.vue";
import BalancingApprovalRejectConfirmation from "@/components/modals/balancing-approval-reject-confirmation.vue";
import BalancingApprovalBulkActionSuccess from "@/components/modals/balancing-approval-bulk-action-success.vue";
import BalancingApprovalBulkActionPartialSuccess from "@/components/modals/balancing-approval-bulk-action-partial-success.vue";
import { serialize } from "object-to-formdata";
import Swal from "sweetalert2";

export default {
  extends: ModelChildIndex,
  components: {
    BalancingApprovalSubmitConfirmation,
    BalancingApprovalApproveConfirmation,
    BalancingApprovalRejectConfirmation,
    BalancingApprovalBulkActionSuccess,
    BalancingApprovalBulkActionPartialSuccess,
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t('balancings.plural'));
      }
    },
    resourceRoute: { default: "sale_notes" },
    modelVariable: { default: "sale_note" },
    tableMinHeight: { type: Number, default: 85.5 }, // 42.5 + 43
    tableHeight: { type: Number, default: 214.5 }, // 42.5 + (43 * 4)
    denyCreate: { type: Boolean, default: true },
    hideTableHeader: { type: Boolean, default: true },
  },
  data() {
    return {
      sortBy: "id",
      sortDesc: true,
      selectedBalancingIds: [],
      selectedBalancingRowsById: {},
      bulkSubmitLoading: false,
      bulkApproveLoading: false,
      bulkRejectLoading: false,
    };
  },
  computed: {
    isAnyBulkActionLoading() {
      return this.bulkSubmitLoading || this.bulkApproveLoading || this.bulkRejectLoading;
    },
    firstSelectedBalancingRow() {
      const firstId = this.selectedBalancingIds[0];
      if (firstId === undefined) return null;
      return this.selectedBalancingRowsById[firstId] || null;
    },
    selectedActionCategory() {
      return this.actionCategoryForModel(this.firstSelectedBalancingRow);
    },
    hasPermissionToSubmitApprovals() {
      return this.$auth.hasPermissionTo("balancing_approvals.create");
    },
    canSubmitSelected() {
      if (!this.hasPermissionToSubmitApprovals) return false;
      return this.selectedBalancingIds.length > 0
          && this.hasAllowedAction(this.firstSelectedBalancingRow, ["submit_balancing_approval"]);
    },
    canApproveSelected() {
      return this.selectedBalancingIds.length > 0
          && this.hasAllowedAction(this.firstSelectedBalancingRow, ["approve_balancing_approval"]);
    },
    canRejectSelected() {
      return this.selectedBalancingIds.length > 0
          && this.hasAllowedAction(this.firstSelectedBalancingRow, ["reject_balancing_approval"]);
    },
    columns() {
      return {
        ["sale_note"]: [
          {
            key: "bulk_select",
            label: "",
            sortable: false,
            class: "text-center",
            thStyle: { width: "1%" },
            tdClass: "align-middle text-center",
            component: {
              props: ["item"],
              methods: {
                onToggle(checked) {
                  this.$emit("toggle-selection", {
                    item: this.item,
                    checked
                  });
                }
              },
              template: `
                <b-form-checkbox
                    v-if="item._canBulkSelect"
                    :checked="item._selected"
                    @change="onToggle"
                    :aria-label="'Select balancing ' + item.id"
                />
              `
            },
            componentListeners: {
              "toggle-selection": payload => this.handleBalancingSelection(payload)
            }
          },
          {
            key: "balancing_transaction.id",
            label: this.lodash.startCase(this.$t(`sale_notes.id`)),
            thStyle: { minWidth: "100px" },
            component: {
              props: ["item"],
              template: `
                <router-link
                    v-if="item.balancing_transaction"
                    :to="{ path: '/sale_notes/' + item.id, query: { redirectTo: 'resume', section: 'balancing' } }"
                >{{ item.balancing_transaction.id }}</router-link>
                <div v-else>-</div>
              `
            }
          },
          {
            key: "id",
            label: "ID Laporan Penjualan",
            thStyle: { minWidth: "150px" },
            component: {
              props: ["item"],
              computed: {
                customLabel() {
                  return `${this.item.type_name}-${this.item.id}`
                }
              },
              template: '<router-link :to="`/sale_notes/${item.id}`">{{ customLabel }}</router-link>'
            }
          },
          {
            key: "ordered_at",
            label: this.lodash.startCase(this.$t(`sale_notes.sales_ordered_at`)),
            thStyle: { minWidth: "120px" },
            formatter: value => (value ? this.$moment(value).format("DD MMM YYYY") : "-")
          },
          {
            key: "customer.name",
            label: this.lodash.startCase(this.$t("sale_notes.partner")),
            thStyle: { minWidth: "150px" },
            component: {
              props: ["item"],
              template: '<div :title="(item.customer || {}).name">{{ (item.customer || {}).name || "-" }}</div>'
            }
          },
          {
            key: "jala_margin",
            label: this.lodash.startCase(this.$t("sale_notes.resumes.JALA_margin")),
            thClass: "text-end",
            tdClass: "text-end",
            thStyle: { minWidth: "150px" },
            component: {
              props: ["item"],
              computed: {
                jalaMargin() {
                  return (this.item.sale_items || []).reduce((carry, { margin }) => carry + (margin ?? 0), 0)
                }
              },
              template: '<div>{{ $numeral(jalaMargin).format("$ 0,0.[00]") }}</div>'
            }
          },
          {
            key: "balancing",
            label: this.lodash.startCase(this.$t("sale_notes.resumes.balancing")),
            thClass: "text-end",
            tdClass: "text-end",
            thStyle: { minWidth: "200px" },
            component: {
              props: ["item"],
              computed: {
                partnerDebt() {
                  if ((this.item.profit_share?.total ?? 0) >= 0) return 0
                  return -this.item.profit_share.total
                },
                partnerMargin() {
                  return this.item.balancing_transaction?.total_debt ?? 0
                },
                label() {
                  return this.partnerDebt
                      ? this.$t('sale_notes.resumes.partner_debt')
                      : this.$t('sale_notes.resumes.transfer_to_partner')
                },
                amount() {
                  return this.partnerDebt || this.partnerMargin
                },
                textClass() {
                  return this.partnerDebt
                      ? 'text-danger'
                      : { 'text-success': this.amount > 0, 'text-danger': this.amount < 0 }
                }
              },
              template: `
                <div :class="textClass">
                  <div style="font-size: 11px; text-transform: uppercase">{{ label }}</div>
                  <b>{{ $numeral(amount).format("$ 0,0.[00]") }}</b>
                </div>
              `
            }
          },
          {
            key: "balancing_approval_status",
            label: `Status ${this.lodash.startCase(this.$t("balancing_approvals.singular"))}`,
            thStyle: { minWidth: "160px" },
            component: {
              props: ["item"],
              template: `
                <status-badge
                    v-if="item.balancing_approval_status"
                    entity="balancing_approval"
                    type="status"
                    :status="item.balancing_approval_status"
                ></status-badge>
                <span v-else>-</span>
              `
            }
          },
        ]
      };
    }
  },
  methods: {
    fetchModels(page = 1) {
      return this.$axios
          .get("/sale_orders", {
            params: {
              page: page,
              per_page: this.perPage,
              search: this.search,
              sort: `${this.sortBy},${this.sortDesc ? "desc" : "asc"}`,
              with: [
                "customer",
                "sale_items",
                "balancing_transaction",
                "profit_share",
                "balancing_approvals.histories.performed_by",
                "balancing_approvals.approval_template.approval_template_steps.approver",
              ].join(","),
              where_has_purchase_plan_id__in: [this.parent.id || this.$route.params.key],
              type__not_in: ["harvest"],
              has_balancing_transaction: 1,
              ...this.filterParams
            }
          })
          .then(response => {
            const {
              data: { data, meta }
            } = response;

            const currentPageIds = data.map(model => this.getModelKey(model));
            const currentPageSelectableIds = new Set(
                data.filter(model => this.canBulkSelect(model)).map(model => this.getModelKey(model))
            );
            const droppedIds = [];
            this.selectedBalancingIds = this.selectedBalancingIds.filter(id => {
              const keep = !currentPageIds.includes(id) || currentPageSelectableIds.has(id);
              if (!keep) droppedIds.push(id);
              return keep;
            });
            droppedIds.forEach(id => this.$delete(this.selectedBalancingRowsById, id));

            this.models = data.map(datum => ({
              ...datum,
              _denyShow: false,
              _denyEdit: true,
              _denyDelete: true,
              _selected: this.selectedBalancingIds.includes(this.getModelKey(datum)),
              _canBulkSelect: false,
            }));
            // Refresh the cached row for any id that's selected and visible on this page,
            // so cross-page consumers (firstSelectedBalancingRow, executeBulkBalancingAction)
            // see up-to-date data instead of a stale snapshot from a previous page load.
            this.models.forEach(model => {
              const id = this.getModelKey(model);
              if (this.selectedBalancingIds.includes(id)) {
                this.$set(this.selectedBalancingRowsById, id, model);
              }
            });
            this.refreshSelectableFlags();
            this.page = Number(meta.current_page);
            this.perPage = Number(meta.per_page);
            this.from = Number(meta.from);
            this.total = Number(meta.total);

            this.stats = [];

            return response;
          })
          .catch(error => {
            this.failedAlert(error);
            return null;
          });
    },
    getAllowedActions(model) {
      return Array.isArray(model?.allowed_actions) ? model.allowed_actions : [];
    },
    hasAllowedAction(model, actions) {
      const allowedActions = this.getAllowedActions(model);
      return actions.some(action => allowedActions.includes(action));
    },
    canBulkSelect(model) {
      return this.hasAllowedAction(model, [
        "submit_balancing_approval",
        "approve_balancing_approval",
        "reject_balancing_approval",
      ]);
    },
    // Submit and approve/reject are mutually exclusive action categories: a row that can
    // be submitted is never also approvable/rejectable in the same state, and vice versa.
    actionCategoryForModel(model) {
      if (!model) return null;
      if (this.hasAllowedAction(model, ["submit_balancing_approval"])) return "submit";
      if (this.hasAllowedAction(model, ["approve_balancing_approval", "reject_balancing_approval"])) return "review";
      return null;
    },
    // Once a row is selected, only rows in the same action category stay selectable, so a
    // selection never mixes "submit" targets with "approve/reject" targets.
    isRowSelectable(model) {
      if (!this.canBulkSelect(model)) return false;

      const category = this.selectedActionCategory;
      if (!category) return true;

      return this.actionCategoryForModel(model) === category;
    },
    refreshSelectableFlags() {
      (this.models || []).forEach(model => {
        model._canBulkSelect = this.isRowSelectable(model);
      });
    },
    latestCycleId(model) {
      const cycles = Array.isArray(model?.balancing_approval_cycles) ? model.balancing_approval_cycles : [];
      const latest = cycles.find(cycle => cycle.is_latest) || cycles[0] || null;
      return latest ? latest.id : null;
    },
    handleBalancingSelection({ item, checked }) {
      const id = this.getModelKey(item);
      item._selected = checked;
      if (checked) {
        if (!this.selectedBalancingIds.includes(id)) {
          this.selectedBalancingIds = [...this.selectedBalancingIds, id];
        }
        this.$set(this.selectedBalancingRowsById, id, item);
      } else {
        this.selectedBalancingIds = this.selectedBalancingIds.filter(selectedId => selectedId !== id);
        this.$delete(this.selectedBalancingRowsById, id);
      }
      this.refreshSelectableFlags();
    },
    clearSelectedBalancing() {
      this.selectedBalancingIds = [];
      this.selectedBalancingRowsById = {};
      (this.models || []).forEach(model => {
        model._selected = false;
      });
      this.refreshSelectableFlags();
    },
    buildActionPayload({ description = null, reason = null, file = null, ...idFields } = {}) {
      const payload = { ...idFields };

      const resolvedText = [description, reason].find((value) => {
        return value !== null && value !== undefined && `${value}`.trim() !== '';
      });

      if (resolvedText !== undefined) {
        payload.description = `${resolvedText}`.trim();
      }

      if (file) {
        payload.file = file;
      }

      return serialize(payload, { indices: true, booleansAsIntegers: true });
    },
    async executeBulkBalancingAction(action, payload = {}) {
      const selectedModels = this.selectedBalancingIds
          .map(id => this.selectedBalancingRowsById[id])
          .filter(Boolean);

      let endpoint;
      let idFields;

      if (action === "submit") {
        endpoint = "/v1/sale_orders/balancing_approvals/bulk-submit";
        idFields = { sale_order_ids: selectedModels.map(model => this.getModelKey(model)) };
      } else {
        endpoint = action === "approve"
            ? "/v1/sale_orders/balancing_approvals/bulk-approve"
            : "/v1/sale_orders/balancing_approvals/bulk-reject";
        idFields = {
          balancing_approval_ids: selectedModels
              .map(model => this.latestCycleId(model))
              .filter(id => id !== null && id !== undefined),
        };
      }

      const response = await this.$axios.post(endpoint, this.buildActionPayload({ ...payload, ...idFields }));
      const summary = this.extractBulkActionSummary(response, selectedModels.length);

      await this.bulkActionSummaryAlert(action, summary);
    },
    bulkActionSummaryAlert(action, summary) {
      if (this.isBulkActionFullySuccessful(summary)) {
        return this.showBulkActionSuccessModal(action, summary);
      }

      return this.showBulkActionPartialSuccessModal(action, summary);
    },
    formatBalancingIdList(ids, { bold = false } = {}) {
      const values = ids.map((id) => (bold ? `<strong>${id}</strong>` : `${id}`));
      if (values.length <= 1) {
        return values.join("");
      }
      if (values.length === 2) {
        return values.join(` ${this.$t('and')} `);
      }
      return `${values.slice(0, -1).join(", ")}, ${this.$t('and')} ${values[values.length - 1]}`;
    },
    openBulkActionModal(action) {
      const total = this.selectedBalancingIds.length;
      const modalRefName = {
        submit: "balancing-approval-submit-confirmation",
        approve: "balancing-approval-approve-confirmation",
        reject: "balancing-approval-reject-confirmation",
      }[action];
      const modalRef = this.$refs[modalRefName];
      if (!modalRef) {
        return Promise.resolve(false);
      }

      const titleKey = `balancing_approvals.modal_messages.bulk_confirm_${action}_title`;
      const msgKey = `balancing_approvals.modal_messages.bulk_confirm_${action}_description`;
      const sortedIds = [...this.selectedBalancingIds].sort((a, b) => a - b);
      const messageParams = { count: total };
      if (action === "approve") {
        messageParams.idList = this.formatBalancingIdList(sortedIds, { bold: true });
      } else if (action === "reject") {
        messageParams.idList = this.formatBalancingIdList(sortedIds);
      }

      return modalRef.show({
        title: this.$t(titleKey),
        message: this.$t(msgKey, messageParams),
      }).then((result) => {
        if (!result) {
          return null;
        }

        const loadingKey = `bulk${this.lodash.upperFirst(action)}Loading`;
        this.$set(this, loadingKey, true);
        return this.executeBulkBalancingAction(action, result === true ? {} : result)
            .catch((error) => {
              this.failedAlert(error);
            })
            .finally(() => {
              this.$set(this, loadingKey, false);
            });
      });
    },
    handleBulkSubmit() {
      if (!this.canSubmitSelected) return;
      return this.openBulkActionModal("submit");
    },
    handleBulkApprove() {
      if (!this.canApproveSelected) return;
      return this.openBulkActionModal("approve");
    },
    handleBulkReject() {
      if (!this.canRejectSelected) return;
      return this.openBulkActionModal("reject");
    },
    handleBulkActionResultModalHidden() {
      this.clearSelectedBalancing();
      // Reload the whole page rather than just this tab's table: a bulk action here
      // also changes the "Riwayat Approval Balancing" card on the parent show.vue page,
      // which is fetched independently and has no other way to be notified.
      this.$router.go(0);
    },
    extractBulkActionSummary(response, totalSelected) {
      const data = response?.data?.data ?? {};
      const successItems = Array.isArray(data.success_items) ? data.success_items : [];
      const failedItems = Array.isArray(data.failed_items) ? data.failed_items : [];
      const successIds = this.normalizeBulkActionIds(successItems);
      const failedIds = this.normalizeBulkActionIds(failedItems);
      const successCount = this.extractBulkActionCount(data, ["success_count"]) ?? (successIds.length > 0 ? successIds.length : null);
      const failedCount = this.extractBulkActionCount(data, ["failed_count"]) ?? (failedIds.length > 0 ? failedIds.length : null);

      return {
        data,
        totalSelected,
        successItems,
        failedItems,
        successIds,
        failedIds,
        successCount: successCount ?? 0,
        failedCount: failedCount ?? 0,
        message: data.message || data?.data?.message || null,
      };
    },
    isBulkActionFullySuccessful(summary) {
      const total = Number(summary?.totalSelected || 0);
      const successCount = Number(summary?.successCount || 0);
      const failedCount = Number(summary?.failedCount || 0);
      const successIdsCount = Array.isArray(summary?.successIds) ? summary.successIds.length : 0;

      if (total <= 0 || failedCount > 0) {
        return false;
      }

      if (successCount >= total || successIdsCount >= total) {
        return true;
      }

      return successCount === 0 && successIdsCount === 0;
    },
    showBulkActionSuccessModal(action, summary) {
      const total = Number(summary?.totalSelected || 0) || this.selectedBalancingIds.length;
      const successCount = Number(summary?.successCount || 0);
      const resolvedCount = successCount > 0 ? successCount : total;
      const modalRef = this.$refs["balancing-approval-bulk-action-success"];
      const title = this.$t(`balancing_approvals.modal_messages.bulk_${action}_success_title`);
      const message = this.$t(`balancing_approvals.modal_messages.bulk_${action}_success_message`, { count: resolvedCount });

      if (!modalRef) {
        return Swal.fire({
          title: title + "!",
          text: message,
          type: "success",
          confirmButtonClass: "btn btn-success btn-fill",
          buttonsStyling: false
        });
      }

      return modalRef.show({ title, message });
    },
    showBulkActionPartialSuccessModal(action, summary) {
      const total = Number(summary?.totalSelected || 0) || this.selectedBalancingIds.length;
      const successCount = Number(summary?.successCount || 0);
      const modalRef = this.$refs["balancing-approval-bulk-action-partial-success"];

      const titleKey = successCount <= 0
          ? `balancing_approvals.modal_messages.bulk_${action}_failed_title`
          : `balancing_approvals.modal_messages.bulk_${action}_partial_success_title`;
      const title = this.$t(titleKey);
      const message = this.$t(`balancing_approvals.modal_messages.bulk_${action}_partial_success_message`, {
        success_count: successCount,
        total,
      });
      const items = this.buildBulkActionResultItems(summary, action);

      if (!modalRef) {
        const safeHtmlItems = items.map((item) => {
          const safeLabel = this.lodash.escape(item.label || `ID ${item.id}`);
          const safeReason = item.reason ? this.lodash.escape(item.reason) : "";
          return `<div>[${this.lodash.escape(item.status)}] ${safeLabel}${safeReason ? ` - ${safeReason}` : ""}</div>`;
        }).join("");

        return Swal.fire({
          title: title + "!",
          html: `
            <div>${message}</div>
            ${items.length ? `<div class="mt-3">${safeHtmlItems}</div>` : ""}
          `,
          type: "warning",
          confirmButtonClass: "btn btn-warning btn-fill",
          buttonsStyling: false
        });
      }

      return modalRef.show({ title, message, items });
    },
    normalizeBulkActionIds(value) {
      if (!Array.isArray(value)) {
        return [];
      }

      return value
          .map((item) => {
            if (item && typeof item === "object") {
              return this.getModelKey(item);
            }

            return item;
          })
          .filter((item) => item !== null && item !== undefined && item !== "");
    },
    extractBulkActionCount(data, keys = []) {
      for (const key of keys) {
        const rawValue = this.lodash.get(data, key);
        const count = Number(rawValue);
        if (Number.isFinite(count)) {
          return count;
        }
      }

      return null;
    },
    normalizeBulkActionText(value) {
      if (Array.isArray(value)) {
        return value
            .map((entry) => this.normalizeBulkActionText(entry))
            .filter((entry) => entry !== null && entry !== undefined && `${entry}`.trim() !== "")
            .join(", ");
      }

      if (value && typeof value === "object") {
        return this.normalizeBulkActionText(value.message ?? value.errors);
      }

      if (value === null || value === undefined) {
        return null;
      }

      const text = `${value}`.trim();
      return text.length > 0 ? text : null;
    },
    extractBulkActionItemReason(item, reasonMap = null, fallbackReason = null) {
      const id = this.getBulkActionItemId(item);
      const mappedReason = reasonMap && typeof reasonMap === "object"
          ? reasonMap[id] ?? reasonMap[String(id)]
          : null;

      return this.normalizeBulkActionText(
          (item && typeof item === "object") ? item.message ?? item.errors : null
      ) || this.normalizeBulkActionText(mappedReason) || this.normalizeBulkActionText(fallbackReason);
    },
    normalizeBulkActionDisplayItems(items, status, reasonMap = null, fallbackReason = null) {
      if (!Array.isArray(items)) {
        return [];
      }

      return items
          .map((item) => {
            const id = this.getBulkActionItemId(item);
            if (id === null || id === undefined || id === "") {
              return null;
            }

            return {
              id,
              status,
              label: this.getBulkActionItemLabel(item),
              reason: this.extractBulkActionItemReason(item, reasonMap, fallbackReason),
            };
          })
          .filter(Boolean);
    },
    buildBulkActionResultItems(summary, action) {
      const fallbackMessage = summary?.message || null;
      const failedReasonMap = this.lodash.get(summary?.data, "errors") || null;
      const successItems = this.normalizeBulkActionDisplayItems(
          summary?.successItems || summary?.data?.success_items,
          "success"
      );
      const failedItems = this.normalizeBulkActionDisplayItems(
          summary?.failedItems || summary?.data?.failed_items,
          "warning",
          failedReasonMap,
          fallbackMessage
      );

      return [...successItems, ...failedItems].map((item, index) => ({
        ...item,
        key: `${item.status}-${item.id}-${index}`,
        action,
      }));
    },
    getBulkActionItemId(item) {
      if (item && typeof item === "object") {
        return this.getModelKey(item) ?? item.id ?? null;
      }

      return item ?? null;
    },
    getBulkActionItemLabel(item) {
      const id = this.getBulkActionItemId(item);
      const model = this.models.find((model) => this.getModelKey(model) === id) || this.selectedBalancingRowsById[id];
      if (!model) {
        return `ID ${id}`;
      }

      return `${model.type_name}-${model.id}`;
    },
  }
};
</script>
